import { type LineItemBuilder } from "../LineItemUpdate";
import { PackageEntrySource } from "~/features/cart/cart/models/CartData";
import { type LineItemMessage } from "../common";
import { groupByFn } from "~/common/utils/reducers";
import { getPackagesBySalesType, hasSuccessor } from "~/features/productDetails/components/PhaseInOut/phaseInOutHelper";
import { getInStockAndInProduction, getTotalStock } from "~/utils/packageEntryUtils";
import {
  PHASE_IN_BUY_ERROR_KEY,
  PHASE_IN_BUY_WARN_KEY,
} from "~/features/productDetails/components/PhaseInOut/constants";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { CART_QTY_EXCEEDS_SALES_ERROR_KEY } from "~/domains/salesCampaign/constants";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { getPackageByCode } from "~/domains/product/utils/productDescriptorUtils";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

export const PHASEOUT2_EXCEED_STOCK_KEY = PHASE_IN_BUY_ERROR_KEY;

const MSG_QUOTE_CHANGED_QTY_NOT_ALLOWED: LineItemMessage = {
  type: "error",
  key: "cart.lineItem.error.quoteChangeNotAllowed",
};

const MSG_QUOTE_CHANGED_QTY_ALLOWED: LineItemMessage = {
  type: "info",
  key: "cart.lineItem.error.quoteChangeAllowed",
};

const phaseInErrorMsg = (quantity: number): LineItemMessage => ({
  type: "error",
  key: PHASE_IN_BUY_ERROR_KEY,
  params: { quantity: "" + quantity },
});
const phaseInWarnMsg = (quantity: number): LineItemMessage => ({
  type: "info",
  key: PHASE_IN_BUY_WARN_KEY,
  params: { quantity: "" + quantity },
});

const phaseout2ExceedStockMsg = (quantity: number): LineItemMessage => ({
  type: "error",
  key: PHASEOUT2_EXCEED_STOCK_KEY,
  params: { quantity: "" + quantity },
});

function validatePhaseout2StockQty(
  lineItems: LineItemBuilder[],
  productDescriptor: ProductDescriptor,
  cartMaterialNos?: Set<string>,
): void {
  const fifteenDigit = productDescriptor.base.fifteenDigit;
  if (!fifteenDigit) {
    return;
  }

  lineItems.forEach((item) => item.removeMessage(PHASEOUT2_EXCEED_STOCK_KEY));

  const lineItemsByPackage = groupByFn(
    lineItems,
    (item) => item.get("packCode"),
    (item) => item,
  );

  for (const [packCode, items] of Object.entries(lineItemsByPackage)) {
    const fifteenDigitEntry = fifteenDigit[packCode];
    if (!fifteenDigitEntry?.successor) {
      continue;
    }

    if (cartMaterialNos?.has(fifteenDigitEntry.successor)) {
      continue;
    }

    const packaging = getPackageByCode(productDescriptor, packCode);
    if (!packaging) {
      continue;
    }

    const availableQty = getInStockAndInProduction(packaging);
    const requestedQty = items.reduce((acc, item) => acc + item.get("quantity"), 0);
    if (requestedQty > availableQty) {
      const msg = phaseout2ExceedStockMsg(availableQty);
      items.forEach((item) => item.addMessage(msg));
    }
  }
}

/**
 * General product, so not all variants for all price references.
 */
export function validateEntireProductQty(
  lineItems: LineItemBuilder[],
  generalProduct: ProductDescriptor,
  cartMaterialNos?: Set<string>,
): void {
  const lineItemsByType = groupByFn(
    lineItems,
    (item) => item.get("source"),
    (item) => item,
  );

  const quoteItems = lineItemsByType[PackageEntrySource.QUOTE];

  if (quoteItems) {
    validateQtyQuote(quoteItems);
  }

  lineItems.forEach((item) => item.removeMessage(CART_QTY_EXCEEDS_SALES_ERROR_KEY));
  const itemsOnSale = lineItems.filter((item) => item.get("saleCampaignEnabled"));
  validateSalesQty(itemsOnSale, generalProduct);

  lineItems.forEach((item) => {
    item.removeMessage(PHASE_IN_BUY_ERROR_KEY);
    item.removeMessage(PHASE_IN_BUY_WARN_KEY);
  });
  const lineItemsByPackage = groupByFn(
    lineItems,
    (item) => item.get("packCode"),
    (item) => item,
  );

  if (isPhaseOut2Enabled()) {
    const eligibleLineItems = lineItems.filter(
      (item) => item.get("source") !== PackageEntrySource.QUOTE && item.get("source") !== PackageEntrySource.CONTRACT,
    );
    validatePhaseout2StockQty(eligibleLineItems, generalProduct, cartMaterialNos);
  } else {
    const { discontinuedPackages } = getPackagesBySalesType(generalProduct);
    discontinuedPackages.forEach((p) => {
      const lineItems = lineItemsByPackage[p.packCode] ?? [];
      const availableQty = getTotalStock(p);
      const requestedQty = lineItems.reduce((acc, item) => acc + item.get("quantity"), 0);
      if (requestedQty > availableQty) {
        const hasSuccessorProduct = hasSuccessor(generalProduct);
        const msg = hasSuccessorProduct ? phaseInErrorMsg(availableQty) : phaseInWarnMsg(availableQty);
        lineItems.forEach((item) => item.addMessage(msg));
      }
    });
  }
}

function validateSalesQty(saleLineItems: LineItemBuilder[], productDescriptor: ProductDescriptor) {
  const lineItemsByPackage = groupByFn(
    saleLineItems,
    (item) => item.get("packCode"),
    (item) => item,
  );
  const salesPerPack = calculateSalesCampaign4PDP({ productDescriptor })?.data?.salePerPack;

  Object.keys(lineItemsByPackage).forEach((packCode) => {
    const lineItems = lineItemsByPackage[packCode];
    const saleData = salesPerPack?.[packCode];
    if (saleData) {
      const totalQty = lineItems.reduce((acc, item) => acc + item.get("quantity"), 0);
      const saleQty = saleData.salesStock;
      if (totalQty > saleQty) {
        // error
        lineItems.forEach((item) => item.addMessage({ type: "error", key: CART_QTY_EXCEEDS_SALES_ERROR_KEY }));
      }
    } else {
      // !saleData
      lineItems.forEach((item) => {
        item.set("saleCampaignCode", "");
        item.set("saleCampaignEnabled", false);
      });
    }
  });
}

function displayLineItemMessage(largerQty: boolean, alterQuoteQuantity: string, minimumQty: string): LineItemMessage {
  // user has permission to both exceed and undercut quantity ("ALLOW_ALL")
  // or the permission to only exceed the quantity ("ALLOW_EXCEED_ONLY")
  if (
    alterQuoteQuantity === AlterQuoteQuantityPermissionType.ALLOW_ALL ||
    (alterQuoteQuantity === AlterQuoteQuantityPermissionType.ALLOW_EXCEED_ONLY && largerQty)
  ) {
    return { ...MSG_QUOTE_CHANGED_QTY_ALLOWED };
  }
  // user does not have permissions ("DENY_ALL")
  // or a user with ("ALLOW_EXCEED_ONLY") permission is trying to undercut
  return {
    ...MSG_QUOTE_CHANGED_QTY_NOT_ALLOWED,
    params: { totalQty: minimumQty },
  };
}
enum AlterQuoteQuantityPermissionType {
  // This enum should be same as enumtype "AlterQuoteQuantityPermissionType" in schaefflercore-items.xml
  ALLOW_ALL = "ALLOW_ALL",
  DENY_ALL = "DENY_ALL",
  ALLOW_EXCEED_ONLY = "ALLOW_EXCEED_ONLY",
}

function validateQtyQuote(lineItems: LineItemBuilder[]): void {
  lineItems.forEach((lineItem) => {
    lineItem.removeMessage(MSG_QUOTE_CHANGED_QTY_NOT_ALLOWED.key);
    lineItem.removeMessage(MSG_QUOTE_CHANGED_QTY_ALLOWED.key);
  });

  const alterQuoteQuantity = window.SCHA.alterQuoteQuantity;
  const groups = groupByFn(lineItems, (item) => `${item.get("quoteNumber")}-${item.get("quoteLine")}`);

  Object.values(groups).forEach((group) => {
    const minimumQty = group[0].get("minimumQty");

    const totalQty = group.reduce((total, item) => total + item.get("quantity"), 0);

    if (totalQty != minimumQty) {
      const msg = displayLineItemMessage(totalQty > minimumQty, alterQuoteQuantity, "" + group[0].get("minimumQty"));
      group.forEach((lineItem) => lineItem.addMessage(msg));
    }
  });
}
